import { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { firestore } from '../database/firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';

/**
 * Role keys returned by this hook (align with UserRoleContext / Firestore collections).
 * superAdmin | admin | moderator | coach | businessadvisor | client | alumni | PlatformEngineer
 */
const ROLES = {
  SUPER_ADMIN: 'superAdmin',
  ADMIN: 'admin',
  MODERATOR: 'moderator',
  COACH: 'coach',
  BUSINESS_ADVISOR: 'businessadvisor',
  CLIENT: 'client',
  ALUMNI: 'alumni',
  PLATFORM_ENGINEER: 'PlatformEngineer',
};

async function roleFromEmailInCollection(collectionName, userEmail) {
  const q = query(collection(firestore, collectionName), where('email', '==', userEmail));
  const snapshot = await getDocs(q);
  return snapshot.empty ? null : snapshot.docs[0];
}

export const useRole = () => {
  const { currentUser } = useAuth();
  const [resolvedRole, setResolvedRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resolveRole = async () => {
      if (!currentUser?.email) {
        setResolvedRole(null);
        setLoading(false);
        return;
      }

      const userEmail = currentUser.email.toLowerCase().trim();
      const uid = currentUser.uid;

      try {
        // Same priority as UserRoleContext: alumni > coach > client > admin > moderator > s_admin > developer
        const snapAlumni = await roleFromEmailInCollection('alumni', userEmail);
        if (snapAlumni) {
          setResolvedRole(ROLES.ALUMNI);
          return;
        }

        let snapCoach = await roleFromEmailInCollection('coaches', userEmail);
        if (!snapCoach && uid) {
          const coachUidSnap = await getDocs(
            query(collection(firestore, 'coaches'), where('uid', '==', uid))
          );
          snapCoach = coachUidSnap.empty ? null : coachUidSnap.docs[0];
        }
        if (snapCoach) {
          setResolvedRole(ROLES.COACH);
          return;
        }

        const snapClient = await roleFromEmailInCollection('clients', userEmail);
        if (snapClient) {
          setResolvedRole(ROLES.CLIENT);
          return;
        }

        const snapAdmin = await roleFromEmailInCollection('admin', userEmail);
        if (snapAdmin) {
          setResolvedRole(ROLES.ADMIN);
          return;
        }

        const modDoc = await roleFromEmailInCollection('moderator', userEmail);
        if (modDoc) {
          const moderatorData = modDoc.data();
          const roleRaw =
            moderatorData.role?.toLowerCase() || moderatorData.type?.toLowerCase() || 'moderator';
          setResolvedRole(
            roleRaw.includes('business') || roleRaw.includes('advisor')
              ? ROLES.BUSINESS_ADVISOR
              : ROLES.MODERATOR
          );
          return;
        }

        const snapSuper = await roleFromEmailInCollection('s_admin', userEmail);
        if (snapSuper) {
          setResolvedRole(ROLES.SUPER_ADMIN);
          return;
        }

        const snapDev = await roleFromEmailInCollection('developer', userEmail);
        if (snapDev) {
          setResolvedRole(ROLES.PLATFORM_ENGINEER);
          return;
        }

        try {
          const snapBaH = await roleFromEmailInCollection('businessAdvisors', userEmail);
          if (snapBaH) {
            setResolvedRole(ROLES.BUSINESS_ADVISOR);
            return;
          }
        } catch (_) {}

        try {
          const snapAdv = await roleFromEmailInCollection('advisors', userEmail);
          if (snapAdv) {
            setResolvedRole(ROLES.BUSINESS_ADVISOR);
            return;
          }
        } catch (_) {}

        if (uid) {
          const userDoc = await getDoc(doc(firestore, 'users', uid));
          if (userDoc.exists()) {
            const raw = userDoc.data()?.role;
            if (raw != null) {
              const r = String(raw).toLowerCase();
              if (r === 'superadmin' || r === 'super_admins') setResolvedRole(ROLES.SUPER_ADMIN);
              else if (r === 'admin' || r === 'admins') setResolvedRole(ROLES.ADMIN);
              else if (r === 'moderator' || r === 'moderators') setResolvedRole(ROLES.MODERATOR);
              else if (
                r === 'businessadvisor' ||
                r === 'business advisor' ||
                r === 'businessadvisors'
              )
                setResolvedRole(ROLES.BUSINESS_ADVISOR);
              else if (r === 'coach' || r === 'businesscoach') setResolvedRole(ROLES.COACH);
              else if (r === 'alumni') setResolvedRole(ROLES.ALUMNI);
              else if (r === 'platformengineer') setResolvedRole(ROLES.PLATFORM_ENGINEER);
              else if (r === 'client' || r === 'entrepreneur' || r === 'entrepreneurs')
                setResolvedRole(ROLES.CLIENT);
              else setResolvedRole(ROLES.CLIENT);
              return;
            }
          }
        }

        setResolvedRole(ROLES.CLIENT);
      } catch (error) {
        setResolvedRole(ROLES.CLIENT);
      } finally {
        setLoading(false);
      }
    };

    resolveRole();
  }, [currentUser?.email, currentUser?.uid]);

  const userRole = useMemo(() => {
    if (!currentUser || loading) return null;
    return resolvedRole || ROLES.CLIENT;
  }, [currentUser, resolvedRole, loading]);

  const isSuperAdmin = useMemo(
    () => userRole === ROLES.SUPER_ADMIN || userRole === 'superAdmins',
    [userRole]
  );
  const isAdmin = useMemo(() => userRole === ROLES.ADMIN, [userRole]);
  const isModerator = useMemo(() => userRole === ROLES.MODERATOR, [userRole]);
  const isCoach = useMemo(() => userRole === ROLES.COACH, [userRole]);
  const isBusinessAdvisor = useMemo(() => {
    return userRole === ROLES.BUSINESS_ADVISOR || userRole === 'business advisor';
  }, [userRole]);
  const isClient = useMemo(
    () => userRole === ROLES.CLIENT || userRole === 'entrepreneur',
    [userRole]
  );
  const isAlumni = useMemo(() => userRole === ROLES.ALUMNI, [userRole]);
  const isPlatformEngineer = useMemo(() => userRole === ROLES.PLATFORM_ENGINEER, [userRole]);

  const isStaff = useMemo(() => {
    return (
      isSuperAdmin || isAdmin || isModerator || isCoach || isBusinessAdvisor || isPlatformEngineer
    );
  }, [isSuperAdmin, isAdmin, isModerator, isCoach, isBusinessAdvisor, isPlatformEngineer]);

  const permissions = useMemo(
    () => ({
      canViewAllSubmissions: isStaff,
      canViewOwnSubmissions: true,
      canViewAuditTool: isStaff,

      canCreateSubmission: true,
      canCreateAudit: isStaff,
      canCreateChecklist: isStaff,

      canUpdateOwnSubmission: submission => {
        if (!submission) return false;
        if (isClient && submission.userId === currentUser?.uid) {
          return submission.status !== 'under_review' && submission.status !== 'approved';
        }
        return isStaff;
      },
      canUpdateAnySubmission: isStaff,
      canUpdateAudit: isStaff,

      canDeleteOwnSubmission: submission => {
        if (!submission) return false;
        if (isClient && submission.userId === currentUser?.uid) {
          return submission.status !== 'under_review' && submission.status !== 'approved';
        }
        return false;
      },
      canDeleteAnySubmission: isStaff,
      canDeleteAudit: isStaff,

      canAuditSubmission: isStaff,
      canApproveSubmission: isStaff,
      canRejectSubmission: isStaff,
      canAddFeedback: isStaff,
      canEditFeedback: feedback => {
        if (!feedback) return false;
        return isStaff || feedback.createdById === currentUser?.uid;
      },
      canDeleteFeedback: feedback => {
        if (!feedback) return false;
        return isStaff || feedback.createdById === currentUser?.uid;
      },

      canExportSubmissions: isStaff,
      canShareSubmission: isStaff,
      canPrintSubmission: true,

      canManageChecklists: isStaff,
    }),
    [isStaff, isClient, currentUser?.uid]
  );

  return {
    userRole,
    isSuperAdmin,
    isAdmin,
    isModerator,
    isCoach,
    isBusinessAdvisor,
    isClient,
    isAlumni,
    isPlatformEngineer,
    isStaff,
    permissions,
  };
};

export default useRole;
